import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import Root from "./Root";
import { ObservabilityErrorBoundary, initFrontendMonitoring } from "./observability/sentry";
import { buildAppRoutes } from "./appRoutes";
import { initTheme } from "@/lib/theme";
import "./i18n";
import "./index.css";

// Initialize PostHog
// Note: In Vite, env vars must be prefixed with VITE_. We check both just in case.
import { Capacitor } from '@capacitor/core';

const posthogKey = import.meta.env.VITE_POSTHOG_API_TOKEN || import.meta.env.POSTHOG_API_TOKEN;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    session_recording: {
      sampleRate: 0.05, // Teamwork Security Guard: 5% session sampling to prevent billing spikes
      maskAllInputs: true, // Apple App Store Guideline 2.5.14 Guard: Required for mobile session replays
    },
    loaded: (ph) => {
      // Register persistent properties to permanently tag events with the client context
      ph.register({
        client_type: Capacitor.isNativePlatform() ? 'native_app' : 'web_browser',
        capacitor_platform: Capacitor.getPlatform(), // 'ios', 'android', or 'web'
      });

      // Optional: don't track localhost development data
      if (import.meta.env.DEV) ph.opt_out_capturing();
    }
  });
}

// Initialize theme
initTheme();
initFrontendMonitoring();

// TODO: Replace polling with WebSocket/SSE push for real-time booking
// notifications and calendar updates once the backend event bus is in place.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: true,
    },
  },
});

const router = createBrowserRouter(buildAppRoutes(<Root />));

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "dummy-client-id";

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ObservabilityErrorBoundary>
      <GoogleOAuthProvider clientId={googleClientId}>
        <QueryClientProvider client={queryClient}>
          {posthogKey ? (
            <PostHogProvider client={posthog}>
              <RouterProvider router={router} />
            </PostHogProvider>
          ) : (
            <RouterProvider router={router} />
          )}
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ObservabilityErrorBoundary>
  </React.StrictMode>,
);
